import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProductReviewJson,
  ProductReviewStatus,
} from "../../types/ProductReviewType";
import Pagination from "../../components/Pagination";
import TextDateTime from "../../components/TextDateTime";
import ProductReviewWriteModal from "./ProductReviewWriteModal";
import Modal from "../../components/Modal";
import { getApiData } from "../../../frontend/api/http";
import { langUrl } from "../../utils/langUrl";

interface ProductPublicReviewListProps {
  productId: number;
}

const ProductPublicReviewList: React.FC<ProductPublicReviewListProps> = ({
  productId,
}) => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<ProductReviewJson[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<number | null>(null);

  const fetchReviews = useCallback(async () => {
    if (!productId || productId <= 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `/productreview/productajax?product_id=${productId}&page=${page}&limit=${limit}`
      );
      const data = await res.json();
      const payload = getApiData<{
        items?: ProductReviewJson[];
        total?: number;
      }>(data);

      if (data.success && Array.isArray(payload?.items)) {
        setReviews(payload.items);
        setTotal(payload.total || 0);
      } else {
        setReviews([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching product reviews:", error);
      setReviews([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [productId, page, limit]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Listen for write review event
  useEffect(() => {
    const handleWriteReview = (event: any) => {
      const customEvent = event as CustomEvent<{ productId: number }>;
      if (customEvent.detail?.productId === productId) {
        setIsWriteModalOpen(true);
      }
    };

    window.addEventListener("product-review-write", handleWriteReview);

    return () => {
      window.removeEventListener("product-review-write", handleWriteReview);
    };
  }, [productId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const renderStars = (rating: number) => {
    return (
      <div className="product-detail-review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className="product-detail-review-star"
            fill={star <= rating ? "currentColor" : "none"}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <>
      {loading && (
        <div className="product-detail-review-loading">
          <div className="product-detail-review-loading-spinner"></div>
          <p className="product-detail-review-loading-text">
            {t("account.reviews.loading")}
          </p>
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <div className="product-detail-review-empty">
          <p className="product-detail-review-empty-text">
            {t("account.reviews.empty_product_reviews_description")}
          </p>
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div className="product-detail-review-list">
          {/* Results Count */}
          <div className="product-detail-review-count">
            <h3 className="product-detail-review-title">
              {t("product.review.title")} ({total})
            </h3>
          </div>

          {/* Reviews List */}
          <div className="product-detail-review-items-wrapper">
            {reviews.map((review) => (
              <div key={review.id} className="product-detail-review-item">
                <div className="product-detail-review-header">
                  <div className="product-detail-review-avatar-wrapper">
                    <div className="product-detail-review-avatar">
                      {review.display_avatar ? (
                        <img
                          src={review.display_avatar}
                          alt={review.display_name || "User"}
                          className="product-detail-review-avatar-image"
                        />
                      ) : (
                        <div className="product-detail-review-avatar-placeholder">
                          {(review.display_name || "U")[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="product-detail-review-content-wrapper">
                    <div className="product-detail-review-user-header">
                      <span className="product-detail-review-user-name">
                        {review.display_name || t("product.blocks.public_review_guest")}
                      </span>
                      <span className="product-detail-review-purchased-badge">
                        <svg
                          className="product-detail-review-badge-icon"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t("product.review.purchased_badge")}
                      </span>
                    </div>
                    <div className="product-detail-review-rating-row">
                      {renderStars(review.rating)}
                      <span className="product-detail-review-date">
                        <TextDateTime
                          format="DD/MM/YYYY"
                          ts={review.date_created}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                {review.comment && (
                  <div className="product-detail-review-comment">
                    {review.comment}
                  </div>
                )}

                {review.admin_reply && (
                  <div className="product-detail-review-admin-reply">
                    <div className="product-detail-review-admin-reply-header">
                      <svg
                        className="product-detail-review-reply-icon"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="product-detail-review-admin-reply-label">
                        {t("account.reviews.display.store_reply_label")}
                      </span>
                    </div>
                    <div className="product-detail-review-admin-reply-text">
                      {review.admin_reply}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="product-detail-review-pagination">
              <Pagination
                total={total}
                page={page}
                limit={limit}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Write Review Modal - always render to ensure event listener is set up */}
      <ProductReviewWriteModal
        isOpen={isWriteModalOpen}
        onClose={() => setIsWriteModalOpen(false)}
        productId={productId}
        onSuccess={(status) => {
          setReviewStatus(status);
          setIsSuccessModalOpen(true);
          setIsWriteModalOpen(false);
          fetchReviews();
        }}
      />

      {/* Success Modal */}
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        title={t("account.reviews.modal.product_review_title")}
        width="max-w-lg"
      >
        <div className="space-y-6">
          {/* Success Message */}
          <div className="text-center py-6">
            {reviewStatus === ProductReviewStatus.STATUS_APPROVED ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <svg
                    className="w-16 h-16 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900">
                  {t("account.reviews.modal.success_approved_message")}
                </p>
              </div>
            ) : reviewStatus === ProductReviewStatus.STATUS_PENDING ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <svg
                    className="w-16 h-16 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900">
                  {t("account.reviews.modal.success_pending_message")}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <svg
                    className="w-16 h-16 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900">
                  {t("account.reviews.modal.success_message_new_product")}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setIsSuccessModalOpen(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t("account.reviews.modal.close_button")}
            </button>
            <a
              href={langUrl('/account/reviews?tab=product')}
              className="flex-1 px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors text-center"
            >
              {t("account.reviews.modal.view_all_product_reviews")}
            </a>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProductPublicReviewList;
